// Must be first: neutralises console.log/debug/info/warn in production builds.
import '@utils/silence-console';

// Polyfill for Promise.withResolvers (required by pdfjs-dist for older browsers)
declare global {
  interface PromiseConstructor {
    withResolvers<T>(): {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: unknown) => void;
    };
  }
}

if (typeof Promise.withResolvers === 'undefined') {
  Promise.withResolvers = <T,>(): {
    promise: Promise<T>;
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: unknown) => void;
  } => {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

import { initSDK } from '@embrace-io/web-sdk';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import axios from 'axios';
import { claimChunkReload } from './utils/chunk-reload';
import { trackPendingWrites } from './utils/app-update';
import './app/globals.css';

// Some screens post through bare `axios` rather than @libs/axios. Instances
// don't inherit the default's interceptors, so each client registers its own.
trackPendingWrites(axios);

// Initialize Embrace SDK as early as possible
// In production, proxy through our own domain to bypass ad blockers
const isProd = (import.meta as any).env?.PROD;
initSDK({
  appID: 'xdscb',
  appVersion: '2.0.0',
  ...(isProd && {
    embraceDataURL: `${window.location.origin}/embrace-data`,
    embraceConfigURL: `${window.location.origin}/embrace-config`,
  }),
});

// A tab left open across a deploy asks for chunk hashes that no longer exist.
// Vite surfaces that as `vite:preloadError`; without a handler the rejected
// import() reaches React as a render error. Reload once to pick up the current
// index.html — the guard keeps it from looping if the asset is really gone.
window.addEventListener('vite:preloadError', (event) => {
  // pdf.js retries a failed worker fetch as a main-thread import(), which
  // lands here whenever the worker asset is unloadable (missing hash, MIME
  // misconfig). That import has its own fallback chain in the PDF pipeline,
  // and reloading instead threw away in-progress work on every file drop —
  // let the rejection propagate to pdf.js rather than treating it as a
  // stale deploy.
  const failedModule = String((event as unknown as { payload?: Error }).payload?.message ?? '');
  if (/pdf\.worker/i.test(failedModule)) return;
  event.preventDefault();
  if (!claimChunkReload()) return;
  window.location.reload();
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
); // CI/CD performance test - 20260318194546
